import type { GameRecord } from '../game/storage/recordStore';
import type { GameModeId } from '../game/content/modes';
import type { SessionEvent, SessionState } from '../game/simulation/types';
import type { GachaAudioPort } from '../audio/GachaAudioDirector';

export type UiAction =
  | Readonly<{ type: 'select-mode'; mode: GameModeId }>
  | Readonly<{ type: 'mode-menu' }>
  | Readonly<{ type: 'economy-modal'; open: boolean }>
  | Readonly<{ type: 'start'; mode?: GameModeId }>
  | Readonly<{ type: 'auto-toggle' }>
  | Readonly<{ type: 'pause-toggle' }>
  | Readonly<{ type: 'restart' }>
  | Readonly<{ type: 'card-use'; generation: number; instanceId: string; targetEntityId: string | null }>
  | Readonly<{ type: 'mute-toggle' }>
  | Readonly<{ type: 'volume'; value: number }>;

export type DangerLevel = 'safe' | 'warning' | 'critical';

type UiListener = (action: UiAction) => void;
type StateListener = (state: SessionState, events: readonly SessionEvent[]) => void;
type LoadingListener = (progress: number, error?: string) => void;
type RecordListener = (record: GameRecord) => void;
type DangerListener = (level: DangerLevel) => void;

class RuntimeBus {
  public gachaAudio: GachaAudioPort | null = null;
  private readonly uiListeners = new Set<UiListener>();
  private readonly stateListeners = new Set<StateListener>();
  private readonly loadingListeners = new Set<LoadingListener>();
  private readonly recordListeners = new Set<RecordListener>();
  private readonly dangerListeners = new Set<DangerListener>();
  private readonly autoListeners = new Set<(enabled: boolean) => void>();

  public onAuto(listener: (enabled: boolean) => void): () => void {
    this.autoListeners.add(listener);
    return () => this.autoListeners.delete(listener);
  }

  public sendAuto(enabled: boolean): void {
    this.autoListeners.forEach(listener => listener(enabled));
  }

  public onUi(listener: UiListener): () => void {
    this.uiListeners.add(listener);
    return () => this.uiListeners.delete(listener);
  }

  public sendUi(action: UiAction): void {
    this.uiListeners.forEach((listener) => listener(action));
  }

  public onState(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  public sendState(state: SessionState, events: readonly SessionEvent[] = []): void {
    this.stateListeners.forEach((listener) => listener(state, events));
  }

  public onLoading(listener: LoadingListener): () => void {
    this.loadingListeners.add(listener);
    return () => this.loadingListeners.delete(listener);
  }

  public sendLoading(progress: number, error?: string): void {
    this.loadingListeners.forEach((listener) => listener(progress, error));
  }

  public onRecord(listener: RecordListener): () => void {
    this.recordListeners.add(listener);
    return () => this.recordListeners.delete(listener);
  }

  public sendRecord(record: GameRecord): void {
    this.recordListeners.forEach((listener) => listener(record));
  }

  public onDanger(listener: DangerListener): () => void {
    this.dangerListeners.add(listener);
    return () => this.dangerListeners.delete(listener);
  }

  public sendDanger(level: DangerLevel): void {
    this.dangerListeners.forEach((listener) => listener(level));
  }
}

export const runtimeBus = new RuntimeBus();
